import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useQueryClient} from '@github-ui/react-query'
import type {CreateRepositoryLabelMutationMutation} from '@github-ui/server-persisted-queries/types/createRepositoryLabelMutation'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {LABELS} from './constants/labels'
import type {LabelsCacheData} from './types'
import {formatFormErrorMessage} from './utils/format-form-error-message'
import {updateLabelsCache} from './utils/update-labels-cache'

type UseCreateLabelOptions = {
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setSubmissionErrors: (v: string) => void
  setDialogOpen: (open: boolean) => void
}

export const TEMP_LABEL_ID = 'temp-new-label-'

export function useCreateLabel({
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setSubmissionErrors,
  setDialogOpen,
}: UseCreateLabelOptions) {
  const queryClient = useQueryClient()

  return useCallback(
    async (
      input: {name: string; description: string; color: string},
      setIsSubmitting: (isSubmitting: boolean) => void,
    ) => {
      const tempId = `${TEMP_LABEL_ID}${crypto.randomUUID()}`
      const previousData = queryKey ? queryClient.getQueryData<LabelsCacheData>(queryKey) : undefined

      const failWith = (message: string) => {
        if (queryKey && previousData) {
          queryClient.setQueryData(queryKey, previousData)
        }
        setSubmissionErrors(message)
        setIsSubmitting(false)
        setDialogOpen(true)
      }

      // Optimistically close the dialog and insert the new label
      setDialogOpen(false)

      if (queryKey) {
        queryClient.setQueryData<LabelsCacheData>(
          queryKey,
          updateLabelsCache(labels => {
            labels.totalCount = (labels.totalCount ?? 0) + 1
            labels.edges.unshift({
              node: {
                id: tempId,
                name: input.name,
                nameHTML: input.name,
                color: input.color,
                description: input.description,
              },
            })
          }),
        )
      }

      try {
        const response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/new`, {
          method: 'POST',
          body: {
            repositoryId,
            name: input.name,
            description: input.description,
            color: input.color,
          },
        })

        if (response.ok) {
          const result: {data: CreateRepositoryLabelMutationMutation} = await response.json()
          const createdLabel = result.data.createLabel?.label
          const errors = result.data.createLabel?.errors

          if (errors && errors.length > 0) {
            failWith(formatFormErrorMessage(errors))
            return
          }

          if (!createdLabel) {
            failWith(LABELS.labelCreateError)
            return
          }

          // Replace the temp node with the server response
          if (queryKey && createdLabel) {
            queryClient.setQueryData<LabelsCacheData>(
              queryKey,
              updateLabelsCache(labels => {
                const edge = labels.edges.find(e => e?.node?.id === tempId)
                if (edge?.node) {
                  edge.node.id = createdLabel.id
                  edge.node.name = createdLabel.name
                  edge.node.nameHTML = createdLabel.nameHTML
                  edge.node.color = createdLabel.color
                  edge.node.description = createdLabel.description
                }
              }),
            )
          }
        } else {
          failWith(LABELS.labelCreateError)
        }
      } catch {
        failWith(LABELS.labelCreateError)
      }
    },
    [repositoryId, repositoryNameWithOwner, queryKey, queryClient, setSubmissionErrors, setDialogOpen],
  )
}
